/**
 * PrimerService — Primer Video System
 * 
 * Plays short informational/educational primer videos before content videos >= 5 minutes.
 * Users on supporter/contributor/vip tiers (and admins) skip primers entirely.
 * 
 * Ported from Burrow Lite HTML (BurrowTV-LITE-1local-asquarerspace-main-template.html)
 * Reference: Lines 8313–8939 of the Lite source
 * 
 * State machine: IDLE → BUFFERING → PLAYING → SWAPPING → IDLE
 */

const SUPPORTER_TIERS = ['supporter', 'contributor', 'vip', 'admin'];

const PrimerService = {
  // ========================
  // Configuration
  // ========================
  config: {
    skipAfterSeconds: 10,
    durationGateSeconds: 300, // 5 minutes — videos shorter than this skip primer
    manifestUrl: 'https://Burrow-Roku-Official-Pullz.b-cdn.net/Manifest%20Files/thought-primer-manifest-v1.json',
    showMessageBanner: true,
    imageHangTimeMs: 3000,   // How long placeholder image stays visible after play detected
    imageFadeTimeMs: 1500,   // CSS transition duration for fade-out
    failureTimeoutMs: 6500,  // If no play event by this time, force-fade placeholder
    primerImageHD: 'https://BurrowPublic.b-cdn.net/images%20burrow-lite/primer-image.jpg',
    primerImageSD: 'https://BurrowPublic.b-cdn.net/images%20burrow-lite/primer-image-sd.jpg',
    primerLibraryId: '575272', // Separate Bunny library for primer videos
  },

  // ========================
  // State
  // ========================
  state: 'IDLE', // IDLE | BUFFERING | PLAYING | SWAPPING
  cachedManifest: null,
  manifestFetching: false,
  manifestPromise: null,

  // ========================
  // Initialization
  // ========================
  init() {
    console.log('[PRIMER] Initializing primer service');
    this.fetchManifest();
    console.log('[PRIMER] Initialization complete');
  },

  // ========================
  // Manifest Fetch & Cache
  // ========================
  async fetchManifest() {
    // Return cached if available
    if (this.cachedManifest) {
      return this.cachedManifest;
    }

    // Return in-flight promise if already fetching
    if (this.manifestFetching && this.manifestPromise) {
      return this.manifestPromise;
    }

    this.manifestFetching = true;
    this.manifestPromise = this._doFetchManifest();
    
    try {
      const result = await this.manifestPromise;
      return result;
    } finally {
      this.manifestFetching = false;
    }
  },

  async _doFetchManifest() {
    try {
      console.log('[PRIMER] Fetching manifest from CDN...');
      const response = await fetch(this.config.manifestUrl, {
        cache: 'default',
        headers: { 'Accept': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Manifest fetch failed: ${response.status}`);
      }

      const manifest = await response.json();
      
      if (!manifest?.primers || !Array.isArray(manifest.primers) || manifest.primers.length === 0) {
        console.warn('[PRIMER] Manifest has no primers');
        this.cachedManifest = null;
        return null;
      }

      console.log(`[PRIMER] Manifest loaded: ${manifest.primers.length} primers available`);
      this.cachedManifest = manifest;
      return manifest;
    } catch (error) {
      console.error('[PRIMER] Manifest fetch error:', error.message);
      this.cachedManifest = null;
      return null;
    }
  },

  // ========================
  // Primer Selection (Weighted Random)
  // ========================
  getRandomPrimer(manifest) {
    if (!manifest?.primers?.length) return null;

    const primers = manifest.primers;
    
    // Calculate total weight
    const totalWeight = primers.reduce((sum, p) => sum + (p['view-rate'] || 1.0), 0);
    
    // Pick a random point in the weight distribution
    let random = Math.random() * totalWeight;
    
    for (const primer of primers) {
      random -= (primer['view-rate'] || 1.0);
      if (random <= 0) {
        return primer;
      }
    }
    
    // Fallback: return last primer
    return primers[primers.length - 1];
  },

  // ========================
  // Gate: Should We Show a Primer?
  // ========================
  shouldShowPrimer(videoData, membershipTier, isLoggedIn, isAdmin) {
    // Phase 1C: Admin bypass removed — admin now has real VIP tier on User doc
    // The membershipTier already reflects their actual tier (e.g., 'vip')
    // so the supporter check below handles them correctly.
    if (isLoggedIn && SUPPORTER_TIERS.includes(membershipTier)) {
      console.log(`[PRIMER] ${membershipTier} tier — skipping primer (interruption-free)`);
      return false;
    }

    // Duration gate — only videos >= 5 minutes
    // Duration lives in content.duration (seconds) in the Bunny video data model
    const duration = videoData?.content?.duration || videoData?.duration || videoData?.length || 0;
    const durationNum = typeof duration === 'string' ? parseFloat(duration) : duration;
    if (isNaN(durationNum) || durationNum < this.config.durationGateSeconds) {
      console.log(`[PRIMER] Video duration ${durationNum}s < ${this.config.durationGateSeconds}s gate — no primer`);
      return false;
    }

    // Manifest check — need available primers
    if (!this.cachedManifest?.primers?.length) {
      console.log('[PRIMER] No primers in manifest — skipping');
      return false;
    }

    console.log(`[PRIMER] Primer will play (tier: ${membershipTier || 'none'}, duration: ${durationNum}s)`);
    return true;
  },

  // ========================
  // Select a Primer for Playback
  // ========================
  async selectPrimer() {
    const manifest = await this.fetchManifest();
    if (!manifest) return null;

    const primer = this.getRandomPrimer(manifest);
    if (!primer) return null;

    return {
      id: primer.id,
      title: primer.title,
      duration: primer.content?.duration || 30,
      hlsUrl: `/api/proxy/stream/${this.config.primerLibraryId}/${primer.id}/playlist.m3u8`,
      viewRate: primer['view-rate'] || 1.0,
    };
  },

  // ========================
  // Primer Image URL (Responsive)
  // ========================
  getPrimerImageUrl() {
    return window.innerWidth > 768 
      ? this.config.primerImageHD 
      : this.config.primerImageSD;
  },

  // ========================
  // State Transitions
  // ========================
  setState(newState) {
    const oldState = this.state;
    this.state = newState;
    console.log(`[PRIMER_STATE] ${oldState} → ${newState}`);
  },

  reset() {
    this.state = 'IDLE';
  },

  // ========================
  // Skip Event Bus
  // Used to sync skip action across overlay + mobile controls instances
  // ========================
  _skipListeners: [],
  
  onSkip(callback) {
    this._skipListeners.push(callback);
    return () => {
      this._skipListeners = this._skipListeners.filter(cb => cb !== callback);
    };
  },

  emitSkip() {
    console.log('[PRIMER] Skip event emitted');
    this._skipListeners.forEach(cb => cb());
  },

  // ========================
  // Video Playing Event Bus
  // Fires when Bunny embed actually starts playing (postMessage detected)
  // ========================
  _videoPlayingListeners: [],
  _videoIsPlaying: false,

  onVideoPlaying(callback) {
    if (this._videoIsPlaying) {
      callback();
    }
    this._videoPlayingListeners.push(callback);
    return () => {
      this._videoPlayingListeners = this._videoPlayingListeners.filter(cb => cb !== callback);
    };
  },

  emitVideoPlaying() {
    if (this._videoIsPlaying) return;
    this._videoIsPlaying = true;
    console.log('[PRIMER] Video playing event emitted');
    this._videoPlayingListeners.forEach(cb => cb());
  },

  resetVideoPlaying() {
    this._videoIsPlaying = false;
    this._videoPlayingListeners = [];
  }
};

export default PrimerService;
